import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import type { UserRole } from "./userTypes";
import {
  isReadOnlyUser,
  isECPAdmin,
  isPlatformAdmin,
  isClientAdmin,
} from "../../components/utils/roleHelpers";

// Re-export role guards so consumers can import everything from one place
export { isReadOnlyUser, isECPAdmin, isPlatformAdmin, isClientAdmin };

// ============================================================================
// Primitive Selectors
// ============================================================================

export const selectUserRole = (state: RootState): UserRole => state.user.role;

export const selectIsPlatformSelected = (state: RootState): boolean => {
  const accountId = state.metadata.accountId;
  return !!(accountId && accountId !== "");
};

export const selectIsClientSelected = (state: RootState): boolean => {
  const clientId = state.metadata.clientId;
  return !!(clientId && clientId !== "");
};

// ============================================================================
// Category Rules
// Defines which categories are available per role + selection context
// ============================================================================

/**
 * ECP Admin category rules:
 * - No Account, No Client → Global
 * - Account only           → Account
 * - Account + Client       → Account, Client
 */
function getEcpAdminCategories(
  isPlatformSelected: boolean,
  isClientSelected: boolean,
): string[] {
  if (!isPlatformSelected && !isClientSelected) return ["Global"];
  if (isPlatformSelected && !isClientSelected)  return ["Account"];
  if (isPlatformSelected && isClientSelected)   return ["Account", "Client"];
  return [];
}

/**
 * Platform Admin category rules:
 * - Account only     → Account
 * - Account + Client → Account, Client
 */
function getPlatformAdminCategories(
  isPlatformSelected: boolean,
  isClientSelected: boolean,
): string[] {
  if (isPlatformSelected && !isClientSelected) return ["Account"];
  if (isPlatformSelected && isClientSelected)  return ["Account", "Client"];
  return [];
}

/**
 * Client Admin category rules:
 * - Account + Client → Client
 */
function getClientAdminCategories(
  isPlatformSelected: boolean,
  isClientSelected: boolean,
): string[] {
  if (isPlatformSelected && isClientSelected) return ["Client"];
  return [];
}

// ============================================================================
// Save Draft Rules
// Defines when Save Draft is disabled per role + selection context
// ============================================================================

function isSaveDraftDisabledForRole(
  role: UserRole,
  isPlatformSelected: boolean,
  isClientSelected: boolean,
): boolean {
  if (isECPAdmin(role))      return false;
  if (isPlatformAdmin(role)) return !isPlatformSelected;
  if (isClientAdmin(role))   return !(isPlatformSelected && isClientSelected);
  return true; // TB_GENERAL_READONLY or unknown role
}

// ============================================================================
// Derived Selectors
// ============================================================================

/**
 * Returns the list of categories available in dropdowns based on role and context.
 */
export const selectAvailableCategories = createSelector(
  [selectUserRole, selectIsPlatformSelected, selectIsClientSelected],
  (role, isPlatformSelected, isClientSelected): string[] => {
    if (isECPAdmin(role))      return getEcpAdminCategories(isPlatformSelected, isClientSelected);
    if (isPlatformAdmin(role)) return getPlatformAdminCategories(isPlatformSelected, isClientSelected);
    if (isClientAdmin(role))   return getClientAdminCategories(isPlatformSelected, isClientSelected);
    return [];
  },
);

/**
 * Returns true when the Save Draft action should be disabled for the current role and context.
 */
export const selectIsSaveDraftDisabledByRole = createSelector(
  [selectUserRole, selectIsPlatformSelected, selectIsClientSelected],
  (role, isPlatformSelected, isClientSelected): boolean =>
    isSaveDraftDisabledForRole(role, isPlatformSelected, isClientSelected),
);

/**
 * Combined permission state — use when multiple values are needed in one component.
 */
export const selectPermissionState = createSelector(
  [
    selectUserRole,
    selectIsPlatformSelected,
    selectIsClientSelected,
    selectAvailableCategories,
    selectIsSaveDraftDisabledByRole,
  ],
  (role, isPlatformSelected, isClientSelected, availableCategories, isSaveDraftDisabledByRole) => ({
    role,
    isPlatformSelected,
    isClientSelected,
    availableCategories,
    isSaveDraftDisabledByRole,
  }),
);
